import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, KeyRound, Link2, Loader2, MessageCircle, Send, X } from 'lucide-react';
import { annoncerErreur, annoncerSucces, useCopie } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════
   L'ACCÈS D'UN PARTICIPANT — où il en est, et comment le rattraper

   Fichier IDENTIQUE dans Front-end/admin et Front-end/user : seul l'appel
   au serveur diffère, et il est passé en `agir`.

   Un membre ajouté ne recevait parfois rien : lien expiré (il vaut
   quelques jours, les membres sont ajoutés bien avant le diagnostic),
   courriel parti dans les indésirables, ou accès générés que personne
   n'avait notés. Aucun écran ne le montrait, aucun bouton n'y remédiait.
   ═══════════════════════════════════════════════════════════════════════ */

const date = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '');

const ETATS = {
  actif: { libelle: 'Accès activé', teinte: 'bg-green-50 text-green-700 border-green-200' },
  lien_envoye: { libelle: 'Lien envoyé', teinte: 'bg-amber-50 text-amber-800 border-amber-200' },
  lien_expire: { libelle: 'Lien expiré', teinte: 'bg-red-50 text-red-700 border-red-200' },
  envoi_echoue: { libelle: 'Envoi refusé', teinte: 'bg-red-50 text-red-700 border-red-200' },
  identifiants_remis: { libelle: 'Jamais connecté', teinte: 'bg-amber-50 text-amber-800 border-amber-200' },
  ferme: { libelle: 'Accès fermé', teinte: 'bg-slate-50 text-slate-600 border-slate-200' },
  sans_compte: { libelle: 'Pas de compte', teinte: 'bg-slate-50 text-slate-600 border-slate-200' },
};

function precision(acces) {
  switch (acces.etat) {
    case 'lien_envoye': return `le ${date(acces.envoye_le)} · valable jusqu’au ${date(acces.expire_le)}`;
    case 'lien_expire': return acces.expire_le ? `depuis le ${date(acces.expire_le)}` : 'à renvoyer';
    case 'envoi_echoue': return 'la messagerie a refusé l’adresse';
    case 'identifiants_remis': return 'identifiants remis, pas encore utilisés';
    case 'ferme': return 'participation terminée';
    default: return '';
  }
}

export function EtatAcces({ acces }) {
  if (!acces) return null;
  const e = ETATS[acces.etat] || ETATS.sans_compte;
  const detail = precision(acces);
  return (
    <span className="inline-flex flex-col gap-0.5 min-w-[9.5rem]" title={acces.erreur || undefined}>
      <span className={`self-start inline-flex whitespace-nowrap px-2 py-0.5 rounded-full border text-[11px] font-semibold ${e.teinte}`}>
        {e.libelle}
      </span>
      {detail && <span className="text-[11px] text-[var(--text-muted)]">{detail}</span>}
    </span>
  );
}

function Fenetre({ titre, onFermer, children }) {
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-label={titre}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold text-[#0A1128]">{titre}</h2>
          <button type="button" onClick={onFermer} className="act act-icone act-tertiaire" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function Partager({ texte, libelleCopie }) {
  const { copier } = useCopie();
  return (
    <div className="flex gap-2">
      <button type="button" className="act act-contour flex-1" onClick={() => copier(texte, libelleCopie)}>
        <Copy className="w-4 h-4" /> Copier
      </button>
      <a className="act act-fort flex-1" target="_blank" rel="noreferrer"
         href={`https://wa.me/?text=${encodeURIComponent(texte)}`}>
        <MessageCircle className="w-4 h-4" /> WhatsApp
      </a>
    </div>
  );
}

/* Les actions possibles dépendent de l'état, pas du goût de l'écran :
   - avec une adresse et un compte jamais activé : renvoyer, ou copier un
     lien à transmettre soi-même ;
   - sans adresse : de nouveaux accès (le serveur refuse si le membre a
     déjà choisi son mot de passe, sauf pour l'équipe) ;
   - compte actif ou fermé : rien à rattraper ici. */
export function ActionsAcces({ participant, agir, onMaj }) {
  const [enCours, setEnCours] = useState('');
  const [resultat, setResultat] = useState(null);
  const acces = participant.acces || {};
  const nom = participant.nom_affichage;

  const lancer = async (cle, corps) => {
    setEnCours(cle);
    try {
      const maj = await agir(participant.id, corps);
      onMaj?.(maj);
      if (maj.lien_activation) setResultat({ lien: maj.lien_activation, expire: maj.lien_expire_le });
      else if (maj.acces_a_transmettre) setResultat({ identifiants: maj.acces_a_transmettre });
      else if (maj.email_sent === false) annoncerErreur(`Le lien n’a pas pu partir${maj.email_error ? ` (${maj.email_error})` : ''}.`);
      else annoncerSucces(`Nouveau lien envoyé à ${participant.email}.`);
    } catch (e) {
      annoncerErreur(e.message || 'L’opération a échoué.');
    } finally {
      setEnCours('');
    }
  };

  const avecAdresse = Boolean(participant.email);
  const aRattraper = ['lien_envoye', 'lien_expire', 'envoi_echoue'].includes(acces.etat);
  const actions = [];
  if (avecAdresse && aRattraper) {
    actions.push({ cle: 'renvoyer', Icone: Send, libelle: 'Renvoyer', aide: `Renvoyer le lien à ${participant.email}`, corps: { renvoyer: true } });
    actions.push({ cle: 'lien', Icone: Link2, libelle: 'Lien à partager', aide: 'Copier un lien à lui transmettre vous-même (WhatsApp…)', corps: { lien: true } });
  } else if (!avecAdresse && acces.etat === 'identifiants_remis') {
    actions.push({ cle: 'regenerer', Icone: KeyRound, libelle: 'Nouveaux accès', aide: 'Générer de nouveaux identifiants à lui transmettre', corps: { renvoyer: true } });
  }

  return (
    <>
      {actions.map(({ cle, Icone, libelle, aide, corps }) => (
        <button key={cle} type="button" onClick={() => lancer(cle, corps)} disabled={!!enCours}
                aria-busy={enCours === cle || undefined} title={aide} aria-label={`${aide} — ${nom}`}
                className="act act-sm act-contour">
          {enCours === cle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icone className="w-4 h-4" />}
          {libelle}
        </button>
      ))}

      {resultat?.lien && (
        <Fenetre titre={`Lien d’accès de ${nom}`} onFermer={() => setResultat(null)}>
          <p className="text-sm text-[var(--text-main)] mb-3">
            Transmettez-lui ce lien : il y choisira son mot de passe. Il ne sert qu’une fois,
            jusqu’au {date(resultat.expire)} au plus tard ; le lien envoyé précédemment ne fonctionne plus.
          </p>
          <p className="font-mono text-[12px] break-all rounded-xl border border-[#e8efe9] bg-[#f6faf7] p-3 mb-4">
            {resultat.lien}
          </p>
          <Partager libelleCopie="Lien copié."
                    texte={`Bonjour ${nom}, voici votre lien d’accès à ASSO-PRO (il vous fera choisir votre mot de passe) : ${resultat.lien}`} />
        </Fenetre>
      )}

      {resultat?.identifiants && (
        <Fenetre titre={`Nouveaux accès de ${nom}`} onFermer={() => setResultat(null)}>
          <p className="text-sm text-[var(--text-main)] mb-3">
            Les anciens ne fonctionnent plus. Ceux-ci ne seront plus affichés : transmettez-les
            maintenant. À sa première connexion, il choisira son propre mot de passe.
          </p>
          <dl className="rounded-xl border border-[#e8efe9] bg-[#f6faf7] p-4 space-y-2 mb-4">
            <div className="flex justify-between gap-3"><dt className="text-xs text-[var(--text-muted)]">Identifiant</dt>
              <dd className="font-mono text-sm text-[#0A1128]">{resultat.identifiants.identifiant}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-xs text-[var(--text-muted)]">Mot de passe</dt>
              <dd className="font-mono text-sm text-[#0A1128]">{resultat.identifiants.mot_de_passe}</dd></div>
          </dl>
          <Partager libelleCopie="Accès copiés."
                    texte={`Bonjour ${nom}, voici vos accès à ASSO-PRO.\nIdentifiant : ${resultat.identifiants.identifiant}\nMot de passe : ${resultat.identifiants.mot_de_passe}`} />
        </Fenetre>
      )}
    </>
  );
}
